import * as Actions from '../actions/docketActions';
import Docket from '../../entities/docket';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type DocketAction = { 
    type: string; 
    docket: Docket | null; 
    docketId: string | Docket; 
    partial: boolean; 
    lastFetch: number; 
    dockets: Docket[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Docket> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Docket[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Docket> | null, firmId: string, userId: string, updates?: Docket[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'docket', 'dockets', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Docket>} data
 * @param {Docket[]} [updates]
 * @returns {ArrayMap<Docket>}
 */
function updateStorage(data: ArrayMap<Docket>, updates?: Docket[]): ArrayMap<Docket> {
	if (logging) console.debug('⮂ docketReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Docket[]} [updates]
 * @returns {ArrayMap<Docket>}
 */
function updateStorageNoReturn(updates?: Docket[]): void {
	if (logging) console.debug('⮂ docketReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Docket[]} [data]
 * @returns {ArrayMap<Docket>}
 */
function createState(data?: Docket[] | ArrayMap<Docket>): ArrayMap<Docket> {
	if (!data) return ArrayMapRegistry.clone<Docket>('docket', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Docket>('docket', data.map(e => e));
    else return ArrayMapRegistry.clone<Docket>('docket', data);
}

function dockets(state: ArrayMap<Docket>, action: DocketAction): ArrayMap<Docket> {
	if (!state) state = createState();

	let st: ArrayMap<Docket>;

	switch (action.type) {
		// action.docket - the new value of docket with id action.id
		// action.docketId - id of the target, used if new value is null
		case Actions.DOCKET_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ docketReducer: FETCH_ONE_SUCCESS', action);

			if (action.docket === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.docketId)) {
				st = state.clone();
				st.set(action.docket);
				// state.clear();

				updateStorageNoReturn([action.docket]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.dockets
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.dockets
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.dockets
			 !partial, !stateEmpty, fetchEmpty	-> D return dockets in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge dockets in state newer than those in action.dockets
		 */
		case Actions.DOCKET_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.dockets.length === 0;
			const fetched = action.dockets.map(docket => new Docket({...docket, persisted: true}));

			if (logging) console.debug(`⮂ docketReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} dockets with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ docketReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ docketReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Docket> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Docket[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(docket => {
					const current = st.get(docket.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && docket.status === 'D') {
							// Remove it from state
							toUpdate.push(docket);
							st.delete(docket);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (docket.addedOn > current.addedOn) {
								toUpdate.push(docket);
								st.set(docket);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (docket.status !== 'D') {
							toUpdate.push(docket);
							st.set(docket);
						}
						else if (logging) console.debug('⮂ docketReducer: partialMerge: skipping deleted docket not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ docketReducer: empty: dockets', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ docketReducer: fullMerge: dockets', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localDocket => {
                    // If this docket has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localDocket.persisted) {
                        const fetchedDocket = st.get(localDocket.id);
                        if (!fetchedDocket || localDocket.addedOn > fetchedDocket.addedOn) st.set(localDocket);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.DOCKET_FETCH_ONE_FAILURE:
			console.error('DOCKET_FETCH_ONE_FAILURE', action.docketId, action.err);

			// No update to storage required
			return state;

		case Actions.DOCKET_FETCH_FAILURE:
			console.error('DOCKET_FETCH_FAILURE', action.docketId, action.err);

			// No update to storage required
			return state;

		case Actions.DOCKET_RESTORE: {
		    let docket = new Docket({...action.docket});
            st = state.clone();

            const existing = st.get(docket.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (docket.addedOn >= existing.addedOn) {
                    st.set(docket);
                    return updateStorage(st, [docket]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (docket.status === 'D') return state;

                st.set(docket);
                return updateStorage(st, [docket]);
            }
        }
            
		case Actions.DOCKET_SAVE: {
		    // console.debug('⮂ DOCKET_SAVE reducer', action.docket?.id);
		    
			// clone the docket
			let docket = new Docket({...action.docket});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new docket in the state
			// If this is a create, add to the queue
			const _docket = st.get(docket.id);

			if (!_docket) st.set(docket);
			else {
				if (docket.addedOn >= _docket.addedOn) st.set(docket);
			}

			return updateStorage(st, [docket]);
		}

		case Actions.DOCKET_SAVE_MANY: {
		    if (logging) console.debug('⮂ DOCKET_SAVE_MANY reducer', action.dockets.map(docket => docket.id).join(','));
		    
			// clone the dockets
			let toProcess = action.dockets.map(docket => new Docket(docket));

			st = state.clone();
			// state.clear();

			const updates: Docket[] = [];

			toProcess.forEach(docket => {
				// Add or update the new docket in the state
				// If this is a create, add to the queue
				const existing = st.get(docket.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (docket.status !== 'D') {
						st.push(docket);
						updates.push(docket);
					}
					else if (logging) console.debug('⮂ DOCKET_SAVE_MANY: skipping deleted docket not in state');
				}
				else {
					if (docket.addedOn >= existing.addedOn) {
						st.set(docket);
						updates.push(docket);
					}
					else if (logging) console.debug('⮂ Skipping, existing docket had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DOCKET_REMOVE: {
			// clone the docket
			const docket = new Docket({...action.docket});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(docket);

			return updateStorage(st, [docket]);
		}

		case Actions.DOCKET_RESTORE_FAILURE:
		case Actions.DOCKET_SAVE_FAILURE: {
		    console.warn('DOCKET_SAVE_FAILURE reducer', action.docket?.id);
		    if (!action.docket?.id) {
				console.warn('DOCKET_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const docket = ensure(Docket, action.docket);
			if (!docket) {
				console.error('Could not find docket after call to save');
				return state;
			}

			st = state.clone();

			const _docketRef = state.get(docket.id);

			if (_docketRef && (_docketRef.addedOn === docket.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _docket = new Docket({..._docketRef});
				_docket.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_docket.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('docket', _docket).then(r => {
						if (r.isErr()) console.error(`⮂ docketReducer: failed to save docket with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ docketReducer: saved docket with fatalError info');
					}).catch(err => console.error(`⮂ docketReducer: failed to save docket with fatalError info: ${err.message}`));

				st.set(_docket);
			}

			return updateStorage(st);
		}

		case Actions.DOCKET_SAVE_MANY_FAILURE: {
		    console.warn('DOCKET_SAVE_MANY_FAILURE reducer', action.dockets.map(docket => docket.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.DOCKET_RESTORE_SUCCESS:
		case Actions.DOCKET_SAVE_SUCCESS: {
			if (!action.docket?.id) {
				console.warn('DOCKET_SAVE_SUCCESS reducer');
                return state;
			}

			const docketRef = state.get(action.docket?.id);
			if (!docketRef) {
				console.warn('Could not find docket after call to save');
                return state;
			}

			st = state.clone();

			if (action.docket.addedOn >= docketRef.addedOn) {
				st.set(action.docket);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const docket = new Docket({...docketRef, persisted: true});
				st.set(docket);
			}

			return updateStorage(st, [action.docket]);
		}

		case Actions.DOCKET_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ DOCKET_SAVE_MANY_SUCCESS reducer', action.dockets.map(docket => docket.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const dockets: Docket[] = [];

			action.dockets.forEach(e => {
				const docketRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!docketRef) console.warn('Could not find docket after call to save');
				else if (e.addedOn >= docketRef.addedOn) {
				    st.set(e);
				    dockets.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const docket = new Docket({...docketRef, persisted: true});
					st.set(docket);
					dockets.push(docket);
				}
			});

			return updateStorage(st, dockets);
		}

		case Actions.DOCKET_REMOVE_FAILURE: {
		    console.warn('DOCKET_REMOVE_FAILURE action', action.docket?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.DOCKET_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.DOCKET_RESTORE_MANY: {
			if (logging) console.debug('⮂ DOCKET_RESTORE_MANY reducer', action.dockets.map(docket => docket.id).join(','));

			const toProcess = action.dockets.map(docket => new Docket({...docket}));
			st = state.clone();

			const updates: Docket[] = [];

			toProcess.forEach(docket => {
				const existing = st.get(docket.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (docket.addedOn >= existing.addedOn) {
						st.set(docket);
						updates.push(docket);
					}
					else if (logging) console.debug('⮂ DOCKET_RESTORE_MANY: skipping older version of docket');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (docket.status !== 'D') {
						st.set(docket);
						updates.push(docket);
					}
					else if (logging) console.debug('⮂ DOCKET_RESTORE_MANY: skipping deleted docket not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.DOCKET_REMOVE_MANY: {
			if (logging) console.debug('⮂ DOCKET_REMOVE_MANY reducer', action.dockets.map(docket => docket.id).join(','));

			const toProcess = action.dockets.map(docket => new Docket({...docket}));
			st = state.clone();

			toProcess.forEach(docket => {
				st.delete(docket);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default dockets;